import type {
  ChannelThreadId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { ChannelThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import { useRequiredContext } from "@redotech/react-util/context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { conversationDetailShortcuts } from "@redotech/redo-web/keyboard-shortcuts/shopper-ai-shortcuts";
import { useKeyboardShortcuts } from "@redotech/redo-web/keyboard-shortcuts/use-keyboard-shortcuts";
import { Text } from "@redotech/redo-web/text";
import {
  type ComponentType,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DraftConversationModal } from "../components/draft-conversation-modal";
import { PanelResizer } from "../components/panel-resizer";
import { SupportTicketActionToolbar } from "../components/support-ticket-action-toolbar";
import { SupportTicketCardList } from "../components/support-ticket-card-list";
import { SupportTicketDetailsPanel } from "../components/support-ticket-details-panel";
import { SupportTicketHeader } from "../components/support-ticket-header";
import { ThreadBoundaryDivider } from "../components/thread-boundary-divider";
import { AttentiveThreadView } from "../components/thread-views/attentive-thread-view";
import { ContactFormThreadView } from "../components/thread-views/contact-form-thread-view";
import { EmailThreadView } from "../components/thread-views/email-thread-view";
import { FacebookThreadView } from "../components/thread-views/facebook-thread-view";
import { InstagramThreadView } from "../components/thread-views/instagram-thread-view";
import { PostscriptThreadView } from "../components/thread-views/postscript-thread-view";
import { RedoChatThreadView } from "../components/thread-views/redo-chat-thread-view";
import { SmsThreadView } from "../components/thread-views/sms-thread-view";
import { TiktokThreadView } from "../components/thread-views/tiktok-thread-view";
import { useAutoAdvanceSupportTicket } from "../hooks/use-auto-advance-support-ticket";
import { useMarkSupportTicketAsRead } from "../hooks/use-mark-support-ticket-as-read";
import { usePresenceViewing } from "../hooks/use-notify-presence";
import { useSupportTicketView } from "../hooks/use-support-ticket-view";
import { useSupportTicketViewLayout } from "../hooks/use-support-ticket-view-layout";
import { SupportTicketDraftsProvider } from "../providers/support-ticket-drafts-context";
import { useSupportTicketsTableContext } from "../table/support-tickets-table-context";
import { groupThreadsForRendering } from "../utils/thread-render-groups";
import * as styles from "./support-ticket-view.module.css";

const SupportTicketCardListPanel = memo(function SupportTicketCardListPanel({
  onCollapse,
}: {
  onCollapse: () => void;
}) {
  return <SupportTicketCardList onCollapse={onCollapse} />;
});

interface SupportTicketViewProps {
  supportTicketId: SupportTicketId;
}

/**
 * Displays one support ticket with its channel threads, context panels, and actions.
 */
export const SupportTicketView = memo(function SupportTicketView({
  supportTicketId,
}: SupportTicketViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const team = useRequiredContext(TeamContext);

  const { conversation, threads, isLoading, customerName, latestMessageTime } =
    useSupportTicketView(supportTicketId);
  const renderableThreads = useMemo(
    () => groupThreadsForRendering(threads),
    [threads],
  );

  const {
    leftPanelOpen,
    toggleLeftPanel,
    rightPanelOpen,
    toggleRightPanel,
    leftPanelWidth,
    rightPanelWidth,
    handleLeftResize,
    handleRightResize,
    handleLeftResizeEnd,
    handleRightResizeEnd,
  } = useSupportTicketViewLayout();

  const [isLeftResizing, setIsLeftResizing] = useState(false);
  const [isRightResizing, setIsRightResizing] = useState(false);

  const handleLeftResizeStart = useCallback(() => setIsLeftResizing(true), []);
  const handleLeftResizeEndWithFlag = useCallback(() => {
    setIsLeftResizing(false);
    handleLeftResizeEnd();
  }, [handleLeftResizeEnd]);
  const handleRightResizeStart = useCallback(
    () => setIsRightResizing(true),
    [],
  );
  const handleRightResizeEndWithFlag = useCallback(() => {
    setIsRightResizing(false);
    handleRightResizeEnd();
  }, [handleRightResizeEnd]);

  const enterTransition = "225ms cubic-bezier(0.0, 0, 0.2, 1)";
  const exitTransition = "195ms cubic-bezier(0.4, 0, 0.6, 1)";
  const leftTransition = isLeftResizing
    ? "0ms"
    : leftPanelOpen
      ? enterTransition
      : exitTransition;
  const rightTransition = isRightResizing
    ? "0ms"
    : rightPanelOpen
      ? enterTransition
      : exitTransition;

  useAutoAdvanceSupportTicket(supportTicketId);
  useMarkSupportTicketAsRead(supportTicketId);
  usePresenceViewing(supportTicketId);

  const { data, openSupportTicket } = useSupportTicketsTableContext();

  const navigateToPrev = useCallback(() => {
    const currentIndex = data.findIndex((c) => c.id === supportTicketId);
    if (currentIndex > 0) {
      openSupportTicket(data[currentIndex - 1]!.id);
    }
  }, [data, supportTicketId, openSupportTicket]);

  const navigateToNext = useCallback(() => {
    const currentIndex = data.findIndex((c) => c.id === supportTicketId);
    if (currentIndex !== -1 && currentIndex < data.length - 1) {
      openSupportTicket(data[currentIndex + 1]!.id);
    }
  }, [data, supportTicketId, openSupportTicket]);

  useKeyboardShortcuts({
    bindings: useMemo(
      () => [
        {
          shortcut: conversationDetailShortcuts.NAVIGATE_UP,
          handler: navigateToPrev,
        },
        {
          shortcut: conversationDetailShortcuts.NAVIGATE_DOWN,
          handler: navigateToNext,
        },
      ],
      [navigateToPrev, navigateToNext],
    ),
    scope: "conversationDetail",
  });

  const [showSpinner, setShowSpinner] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setShowSpinner(false);
      return;
    }
    const timeout = setTimeout(() => setShowSpinner(true), 1000);
    return () => clearTimeout(timeout);
  }, [isLoading]);

  const handleBack = useCallback(
    () => navigate(`/stores/${team._id}/support-v2${location.search}`),
    [navigate, team._id, location.search],
  );

  if (conversation?.status === "DRAFT") {
    const draftThread = threads[0];
    if (!draftThread) {
      return null;
    }
    return (
      <DraftConversationModal
        conversation={conversation}
        onClose={handleBack}
        onCreated={(id) =>
          navigate(`/stores/${team._id}/support-v2/${id}${location.search}`)
        }
        thread={draftThread}
      />
    );
  }

  const showContent = !isLoading && conversation;

  return (
    <Flex dir="column" gap="none" height="full" overflow="hidden" w="full">
      <Flex flex={1} gap="none" style={{ minHeight: 0 }}>
        <Flex
          className={styles.panelOuter}
          overflow="hidden"
          shrink={0}
          style={{
            width: leftPanelOpen ? leftPanelWidth : 0,
            transition: `width ${leftTransition}`,
          }}
        >
          <Flex
            borderColor="secondary"
            borderRightWidth="1px"
            borderStyle="solid"
            className={styles.panelInner}
            dir="column"
            inert={!leftPanelOpen}
            style={{
              width: leftPanelWidth,
              transform: leftPanelOpen ? "translateX(0)" : "translateX(-100%)",
              transition: `transform ${leftTransition}`,
            }}
          >
            <SupportTicketCardListPanel onCollapse={toggleLeftPanel} />
          </Flex>
        </Flex>
        <PanelResizer
          onResize={handleLeftResize}
          onResizeEnd={handleLeftResizeEndWithFlag}
          onResizeStart={handleLeftResizeStart}
          open={leftPanelOpen}
          transition={leftTransition}
        />

        {showContent ? (
          <SupportTicketDraftsProvider
            hasDraft={conversation.numDrafts > 0}
            key={supportTicketId}
            supportTicketId={supportTicketId}
          >
            <Flex
              className={styles.fadeIn}
              dir="column"
              style={{ flex: 1, minWidth: 0 }}
            >
              <SupportTicketHeader
                actionToolbar={
                  <SupportTicketActionToolbar
                    conversation={conversation}
                    supportTicketId={supportTicketId}
                  />
                }
                customerName={customerName}
                latestMessageTime={latestMessageTime}
                leftPanelOpen={leftPanelOpen}
                onBack={handleBack}
                onToggleLeftPanel={toggleLeftPanel}
                onToggleRightPanel={toggleRightPanel}
                rightPanelOpen={rightPanelOpen}
                ticketNumber={conversation.ticketNumber}
              />
              <Flex
                bgColor="secondary"
                dir="column"
                grow={1}
                overflowY="auto"
                p="xl"
                style={{ minHeight: 0 }}
              >
                <Flex dir="column" gap="xl" grow={1}>
                  {renderableThreads.length === 0 ? (
                    <Flex align="center" grow={1} justify="center">
                      <Text fontSize="md" textColor="secondary">
                        No messages in this thread
                      </Text>
                    </Flex>
                  ) : (
                    renderableThreads.flatMap(
                      ({ thread, messageThreadIds }, index) => [
                        ...(index > 0
                          ? [
                              <ThreadBoundaryDivider
                                key={`divider-${thread.id}`}
                              />,
                            ]
                          : []),
                        <ThreadRenderer
                          key={thread.id}
                          messageThreadIds={messageThreadIds}
                          supportTicketId={supportTicketId}
                          thread={thread}
                        />,
                      ],
                    )
                  )}
                </Flex>
              </Flex>
            </Flex>

            <PanelResizer
              onResize={handleRightResize}
              onResizeEnd={handleRightResizeEndWithFlag}
              onResizeStart={handleRightResizeStart}
              open={rightPanelOpen}
              transition={rightTransition}
            />
            <Flex
              className={styles.panelOuter}
              overflow="hidden"
              shrink={0}
              style={{
                width: rightPanelOpen ? rightPanelWidth : 0,
                transition: `width ${rightTransition}`,
              }}
            >
              <Flex
                bgColor="primary"
                borderColor="secondary"
                borderLeftWidth="1px"
                borderStyle="solid"
                className={styles.panelInner}
                dir="column"
                inert={!rightPanelOpen}
                overflow="hidden"
                style={{
                  width: rightPanelWidth,
                  transform: rightPanelOpen
                    ? "translateX(0)"
                    : "translateX(100%)",
                  transition: `transform ${rightTransition}`,
                }}
              >
                <SupportTicketDetailsPanel
                  conversation={conversation}
                  onCollapse={toggleRightPanel}
                />
              </Flex>
            </Flex>
          </SupportTicketDraftsProvider>
        ) : (
          <Flex align="center" grow={1} justify="center">
            {showSpinner && <RedoSpinner />}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});

interface ThreadRendererProps {
  messageThreadIds?: readonly ChannelThreadId[];
  supportTicketId: SupportTicketId;
  thread: ChannelThread;
}

type ThreadViewComponent = ComponentType<ThreadRendererProps>;

const emailThreadView: ThreadViewComponent = EmailThreadView;
const facebookThreadView: ThreadViewComponent = FacebookThreadView;
const instagramThreadView: ThreadViewComponent = InstagramThreadView;
const redoChatThreadView: ThreadViewComponent = RedoChatThreadView;

const threadViewByIntegrationType = {
  gmail: emailThreadView,
  outlook: emailThreadView,
  facebook: facebookThreadView,
  instagram: instagramThreadView,
  tiktok: TiktokThreadView,
  attentive: AttentiveThreadView,
  telnyx_sms: SmsThreadView,
  postscript: PostscriptThreadView,
  redoChatAi: redoChatThreadView,
  redoChatFlow: redoChatThreadView,
  redoChatLive: redoChatThreadView,
  contactForm: ContactFormThreadView,
} satisfies Record<ChannelThread["integrationType"], ThreadViewComponent>;

const ThreadRenderer = memo(function ThreadRenderer({
  messageThreadIds,
  supportTicketId,
  thread,
}: ThreadRendererProps) {
  const ThreadView = threadViewByIntegrationType[thread.integrationType];
  return (
    <ThreadView
      messageThreadIds={messageThreadIds}
      supportTicketId={supportTicketId}
      thread={thread}
    />
  );
});
